import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import DreamDriftTitle from 'src/game/dreamDrift/components/DreamDriftTitle';
import { dreamDriftTheme } from 'src/game/dreamDrift/dreamDrift.theme';
import PopupMatch2Base, {
  type PopupMatch2BaseInitOptions,
} from 'src/game/minigame/match2-timestep/components/PopupMatch2Base';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  dreamDriftGetEventRemainingTime,
  dreamDriftGetFinalNode,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import { user } from 'src/StateObserver';

/** Primary presentation and action available from the Dream Drift intro. */
export type DreamDriftIntroMode = 'begin' | 'revive' | 'cure';

/** Localized intro copy selected for each pet gate. */
const descriptionKeyByMode: Record<DreamDriftIntroMode, string> = {
  begin: 'dreamDrift.intro.description',
  revive: 'dreamDrift.intro.reviveDescription',
  cure: 'dreamDrift.intro.cureDescription',
};

/** Localized CTA selected for each pet gate. */
const ctaKeyByMode: Record<DreamDriftIntroMode, string> = {
  begin: 'dreamDrift.intro.begin',
  revive: 'dreamDrift.intro.revive',
  cure: 'dreamDrift.intro.cure',
};

/** Arguments configuring the Dream Drift intro for the current entry gate. */
export type PopupDreamDriftIntroInitArgs = PopupMatch2BaseInitOptions & {
  mode: DreamDriftIntroMode;
};

/** Close callback reporting the selected intro action or dismissal. */
type PopupDreamDriftIntroClose = (action: DreamDriftIntroMode | false) => void;

/** Dream Drift signup popup introducing the current event theme and rules. */
export default class PopupDreamDriftIntro extends PopupMatch2Base<PopupDreamDriftIntroClose> {
  /** Theme supplying the signup artwork for the current Dream Drift event. */
  private readonly theme = dreamDriftTheme();

  /** Current action and copy presented by the signup popup. */
  private mode: DreamDriftIntroMode = 'begin';

  /** Live countdown showing the remaining duration of the event window. */
  private readonly timer: Match2Timer;

  /** Event or revive guidance displayed above the primary action. */
  private readonly description: LangBitmapFontTextView;

  /**
   * Create the framed Dream Drift introduction composition.
   *
   * @param opts Popup parent and typed close callback supplied by the registry.
   */
  constructor(opts: { superview: View; close: PopupDreamDriftIntroClose }) {
    // Reuse the Match-2 intro frame and standard popup controls.
    super({
      superview: opts.superview,
      width: 593,
      height: 814,
      panelImageOpts: {
        image: 'assets/ui/match2/common/panel_intro_alt.png',
      },
      offsetY: 45,
      close: () => opts.close(false),
      cta: {
        localeText: () => i18n('dreamDrift.intro.begin'),
        positioning: 'edge',
        width: 460,
        offsetY: -95,
      },
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_intro',
      },
    });

    // Report whichever action mode was configured when the CTA is selected.
    this.opts.cta!.onClick = async () => opts.close(this.mode);

    // Place the themed square behind the transparent opening in the frame.
    new ImageScaleView({
      superview: this.panelView,
      image: this.theme.getIntroImagePath(),
      width: 510,
      height: 510,
      x: this.panel.style.width / 2,
      y: 302,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    // Reuse the localized Dream Drift title above the framed illustration.
    new DreamDriftTitle({
      superview: this.panel,
      x: this.panel.style.width / 2,
      y: -5,
      scale: 0.86,
      centerOnOrigin: true,
      zIndex: 1,
    });

    // Display the remaining time for the active Dream Drift event window.
    this.timer = new Match2Timer({
      superview: this.panel,
      x: this.panel.style.width / 2,
      y: 555,
      centerOnOrigin: true,
    });
    this.timer.setRemainingTimeGetter((now) =>
      dreamDriftGetEventRemainingTime(user(), now),
    );

    // Stop the timer scheduler whenever the popup leaves the view hierarchy.
    this.timer.addListener('ViewRemoved', () => this.timer.stopAutoUpdate());

    // Explain the event goal directly above the primary action.
    this.description = new LangBitmapFontTextView({
      superview: this.panel,
      localeText: () =>
        i18n(descriptionKeyByMode[this.mode], {
          count: dreamDriftGetFinalNode(),
        }),
      x: this.panel.style.width / 2,
      y: 615,
      width: 530,
      height: 82,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Body',
      size: 22,
      color: '#BF8147',
    });
  }

  /**
   * Start the event countdown and open the configured popup.
   *
   * @param opts Presentation mode and legacy Match-2 popup options.
   */
  public init(opts: PopupDreamDriftIntroInitArgs): void {
    // Configure the primary action before the popup becomes visible.
    this.mode = opts.mode;
    this.buttonCta!.localeText = () => i18n(ctaKeyByMode[this.mode]);
    this.description.notifyLocaleTextChanged();

    // Begin refreshing the visible event deadline once per second.
    this.timer.autoUpdate();

    // Open the standard framed popup after its content is configured.
    super.init(opts);
  }

  /**
   * Load the themed illustration and localized Dream Drift title assets.
   *
   * @returns Asset group required before the popup becomes visible.
   */
  protected getAssetsGroup(): AssetGroup {
    // Load the current theme artwork alongside both localized title variants.
    return new AssetGroup([
      this.theme.getIntroImagePath(),
      ...AssetGroup.constructURLs(['assets/ui/dreamDrift/common']),
    ]);
  }
}
